import { supabase } from "@/integrations/supabase/client";

/**
 * Récupère le jeton d'accès courant sans jamais bloquer l'interface.
 *
 * `supabase.auth.getSession()` s'appuie sur le verrou `navigator.locks` : si un
 * autre onglet (ou un rafraîchissement de jeton interrompu) conserve le verrou,
 * la promesse ne se résout jamais. Les gestionnaires de paiement restaient alors
 * bloqués avant même d'émettre la moindre requête réseau, sans erreur visible.
 *
 * On borne donc l'attente, avec repli sur la session persistée en localStorage.
 */
export async function getAccessTokenSafe(timeoutMs = 2500): Promise<string | null> {
  const fromSdk = supabase.auth
    .getSession()
    .then(({ data }) => data.session?.access_token ?? null)
    .catch(() => null);

  const timed = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));

  const token = await Promise.race([fromSdk, timed]);
  if (token) return token;

  return readTokenFromStorage();
}

/** Lecture directe du jeton persisté par le client Supabase (sans verrou). */
function readTokenFromStorage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const token = parsed?.access_token ?? parsed?.currentSession?.access_token ?? null;
      if (typeof token === "string" && token.length > 0) return token;
    }
  } catch {
    /* stockage indisponible ou contenu illisible */
  }
  return null;
}

/**
 * Borne une opération d'authentification qui s'appuie sur `navigator.locks`.
 * Renvoie `null` si l'opération n'a pas abouti dans le délai imparti, au lieu
 * de laisser l'appelant suspendu indéfiniment.
 */
export async function withAuthTimeout<T>(op: Promise<T>, timeoutMs = 2500): Promise<T | null> {
  const timed = new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs));
  try {
    return await Promise.race([op.catch(() => null), timed]);
  } catch {
    return null;
  }
}

/** Date d'expiration (epoch, secondes) lue dans la charge utile du jeton. */
export function readTokenExpiry(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      ),
    );
    return typeof json?.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}
